import React from 'react';
import './Experience.css';

const Experience = () => {
  const experienceData = [
    {
      period: "July 2024 - Present",
      duration: "Current",
      position: "Assistant Professor",
      institution: "Pimpri Chinchwad University",
      location: "Pune, Maharashtra",
      type: "current",
      responsibilities: [
        "Teaching management subjects to postgraduate students",
        "Developing curriculum for new age management courses",
        "Mentoring students for research and industry projects",
        "Contributing to university academic committees"
      ],
      achievements: ["Joined prestigious university in Maharashtra", "Part of curriculum development team"],
      icon: "🏫"
    },
    {
      period: "Oct 2022 - June 2024",
      duration: "1 year 9 months",
      position: "Assistant Professor",
      institution: "Lingaya's Vidyapeeth",
      location: "Faridabad, Haryana",
      type: "previous",
      responsibilities: [
        "Taught MBA and BBA students in management department",
        "Served as Departmental Exam Coordinator",
        "Managed Time Table for School of Management",
        "Coordinated MOOC/SWAYAM programs"
      ],
      achievements: ["Promoted to key administrative roles", "Enhanced student learning experience"],
      icon: "🎓"
    },
    {
      period: "Aug 2019 - Sep 2022",
      duration: "3 years 2 months",
      position: "Assistant Professor & Acting HOD",
      institution: "Dr. Om Prakash Group of Institutions",
      location: "Farrukhabad, Uttar Pradesh",
      type: "previous",
      responsibilities: [
        "Headed MBA department as Acting HOD",
        "Managed Exam Cell operations",
        "Handled AICTE & AKTU administrative work",
        "Taught 18+ hours per semester"
      ],
      achievements: ["Promoted to Acting HOD position", "Successfully managed departmental operations"],
      icon: "👨‍💼"
    },
    {
      period: "Aug 2017 - July 2019",
      duration: "2 years",
      position: "Assistant Professor",
      institution: "Signa Institute of Professional Studies",
      location: "Kanpur, Uttar Pradesh",
      type: "previous",
      responsibilities: [
        "Taught management subjects for BBA & BCA programs",
        "Developed course content for professional studies",
        "Mentored students for industry readiness",
        "Participated in academic planning"
      ],
      achievements: ["Started academic career", "Built foundation in teaching methodologies"],
      icon: "📚"
    }
  ];

  const subjectsTaught = [
    { category: "Marketing", subjects: ["Marketing Management", "Consumer Behavior", "Advertising & Sales Promotion", "Integrated Marketing Communications", "Service Marketing"] },
    { category: "Human Resources", subjects: ["Human Resource Management", "Training & Development", "Industrial Relations & Labor Laws"] },
    { category: "Operations & Analytics", subjects: ["Operations Research", "Business Analytics", "Strategic Management"] }
  ];

  const administrativeRoles = [
    { role: "Departmental Exam Coordinator", institution: "Lingaya's Vidyapeeth" },
    { role: "Time Table In-charge", institution: "School of Management, Lingaya's" },
    { role: "MOOC/SWAYAM Coordinator", institution: "Multiple Institutions" },
    { role: "Acting HOD of MBA", institution: "Dr. Om Prakash Group of Institutions" },
    { role: "Exam Cell In-charge", institution: "Dr. Om Prakash Group of Institutions" },
    { role: "Departmental Sports/NSS Coordinator", institution: "Lingaya's Vidyapeeth" }
  ];

  return (
    <section id="experience" className="experience-section">
      <div className="container">
        {/* Section Header */}
        <div className="section-header">
          <h2 className="section-title">Professional Experience</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">
            8+ years of dedicated service in academic institutions, shaping future leaders through quality education
          </p>
        </div>

        {/* Experience Timeline */}
        <div className="experience-timeline">
          {experienceData.map((exp, index) => (
            <div key={index} className={`timeline-item ${exp.type}`}>
              <div className="timeline-marker">
                <div className="marker-icon">
                  <span>{exp.icon}</span>
                </div>
                <div className="timeline-connector"></div>
              </div>

              <div className="timeline-content">
                <div className="experience-card">
                  {/* Card Header */}
                  <div className="exp-header">
                    <div className="exp-main">
                      <h3 className="position">{exp.position}</h3>
                      <div className="institution-info">
                        <span className="institution">{exp.institution}</span>
                        <span className="location">📍 {exp.location}</span>
                      </div>
                    </div>
                    <div className="exp-period">
                      <span className="period">{exp.period}</span>
                      <span className="duration">{exp.duration}</span>
                    </div>
                  </div>

                  {/* Responsibilities */}
                  <div className="responsibilities">
                    <h4 className="responsibilities-title">Key Responsibilities</h4>
                    <ul className="responsibilities-list">
                      {exp.responsibilities.map((resp, idx) => (
                        <li key={idx} className="responsibility-item">
                          <span className="bullet">▸</span>
                          {resp}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Achievements */}
                  {exp.achievements && (
                    <div className="achievements">
                      <h4 className="achievements-title">Key Achievements</h4>
                      <div className="achievements-list">
                        {exp.achievements.map((achievement, idx) => (
                          <span key={idx} className="achievement-badge">
                            {achievement}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Current Position Badge */}
                  {exp.type === "current" && (
                    <div className="current-badge">
                      <span>Current Position</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Information Grid */}
        <div className="additional-info">
          {/* Subjects Taught */}
          <div className="info-card subjects-card">
            <div className="card-header">
              <h3 className="card-title">Subjects Taught</h3>
              <div className="card-icon">📖</div>
            </div>
            <div className="subjects-grid">
              {subjectsTaught.map((category, index) => (
                <div key={index} className="subject-category">
                  <h4 className="category-title">{category.category}</h4>
                  <ul className="subject-list">
                    {category.subjects.map((subject, idx) => (
                      <li key={idx} className="subject-item">
                        {subject}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Administrative Roles */}
          <div className="info-card roles-card">
            <div className="card-header">
              <h3 className="card-title">Administrative Roles</h3>
              <div className="card-icon">⚙️</div>
            </div>
            <div className="roles-list">
              {administrativeRoles.map((role, index) => (
                <div key={index} className="role-item">
                  <div className="role-dot"></div>
                  <div className="role-content">
                    <span className="role-title">{role.role}</span>
                    <span className="role-institution">{role.institution}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Experience Statistics */}
        <div className="experience-stats">
          <div className="stat-item">
            <div className="stat-icon">🎯</div>
            <div className="stat-content">
              <div className="stat-number">8+</div>
              <div className="stat-label">Years Experience</div>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon">🏫</div>
            <div className="stat-content">
              <div className="stat-number">4</div>
              <div className="stat-label">Institutions</div>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon">📚</div>
            <div className="stat-content">
              <div className="stat-number">15+</div>
              <div className="stat-label">Subjects Taught</div>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <div className="stat-number">6+</div>
              <div className="stat-label">Admin Roles</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;